import type {
  CustomEmailProvider,
  CustomEmailSender,
} from "@redotech/admin-sdk/rpc/schema/support/custom-email-sender";
import { useRequiredContext } from "@redotech/react-util/context";
import { DkimStatus } from "@redotech/redo-marketing-core-common/email/domain-verification";
import { alertOnFailure, toast } from "@redotech/redo-web/alert";
import { RedoAlert } from "@redotech/redo-web/arbiter-components/alert/redo-alert";
import type { RedoBadgeProps } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { RedoModal } from "@redotech/redo-web/arbiter-components/modal/redo-modal";
import {
  RedoTable,
  type RedoTableColumn,
} from "@redotech/redo-web/arbiter-components/tables/redo-table";
import {
  RedoTableBadgeCell,
  RedoTablePaddedCell,
  RedoTableTextCell,
} from "@redotech/redo-web/arbiter-components/tables/redo-table-cells";
import { DisabledTooltip } from "@redotech/redo-web/arbiter-components/tooltip/disabled-tooltip";
import { Card } from "@redotech/redo-web/card";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { memo, useMemo, useState } from "react";
import { RedoAdminRpcClientContext } from "../../../app/redo-admin-rpc-client-provider";
import { SesSetupGuide } from "./ses-setup-guide";

const PROVIDER_LABEL: Record<CustomEmailProvider, string> = {
  postmark: "Postmark",
  ses: "SES",
};

function customEmailSendersQueryKey(teamId: string) {
  return ["custom-email-senders", teamId] as const;
}

function setupStatusBadge(setupStatus: string): RedoBadgeProps {
  return setupStatus === "connected"
    ? { color: "success", text: "Connected", size: "xs" }
    : { color: "gray", text: setupStatus.replace(/_/g, " "), size: "xs" };
}

function dkimBadge(dkimStatus: string): RedoBadgeProps {
  switch (dkimStatus) {
    case DkimStatus.VERIFIED:
      return { color: "success", text: "DKIM verified", size: "xs" };
    case DkimStatus.PENDING:
      return { color: "warning", text: "DKIM pending", size: "xs" };
    case DkimStatus.FAILED:
      return { color: "error", text: "DKIM failed", size: "xs" };
    default:
      return { color: "gray", text: "DKIM not started", size: "xs" };
  }
}

function otherProvider(provider: CustomEmailProvider): CustomEmailProvider {
  return provider === "ses" ? "postmark" : "ses";
}

interface PendingSwitch {
  sender: CustomEmailSender;
  provider: CustomEmailProvider;
}

export const HelpdeskEmailSendersCard = memo(function HelpdeskEmailSendersCard({
  teamId,
}: {
  teamId: string;
}) {
  const client = useRequiredContext(RedoAdminRpcClientContext);
  const queryClient = useQueryClient();
  const [pendingSwitch, setPendingSwitch] = useState<PendingSwitch | null>(
    null,
  );

  const sendersQuery = useQuery({
    queryKey: customEmailSendersQueryKey(teamId),
    queryFn: () => client.getCustomEmailSenders({ teamId }),
  });

  const switchMutation = useMutation({
    mutationFn: ({ sender, provider }: PendingSwitch) =>
      alertOnFailure("Could not switch the sender")(() =>
        client.setCustomEmailSenderProvider({
          teamId,
          integrationId: sender.integrationId,
          provider,
        }),
      ),
    onSuccess: (_result, { sender, provider }) => {
      toast(`${sender.email} now sends through ${PROVIDER_LABEL[provider]}`, {
        variant: "success",
      });
      setPendingSwitch(null);
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: customEmailSendersQueryKey(teamId),
      }),
  });

  const recheckMutation = useMutation({
    mutationFn: (sender: CustomEmailSender) =>
      alertOnFailure("Could not re-check DNS")(() =>
        client.refreshCustomEmailSenderDomainStatus({
          teamId,
          integrationId: sender.integrationId,
        }),
      ),
    onSuccess: (result) => {
      toast(
        `DKIM for ${result.sender.sesReadiness.domain} is ${result.sender.sesReadiness.dkimStatus?.replace(/_/g, " ") ?? "not set up"}`,
        { variant: "success" },
      );
    },
    onSettled: () =>
      queryClient.invalidateQueries({
        queryKey: customEmailSendersQueryKey(teamId),
      }),
  });

  const columns = useMemo<RedoTableColumn<CustomEmailSender>[]>(
    () => [
      {
        key: "email",
        widthMode: { type: "fill", relativeLength: 2 },
        renderHeaderCell: () => <RedoTableTextCell text="Sender" />,
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell leading subtext={row.name} text={row.email} />
        ),
      },
      {
        key: "status",
        widthMode: { type: "hug" },
        renderHeaderCell: () => <RedoTableTextCell text="Status" />,
        renderNormalCell: ({ row }) => (
          <RedoTableBadgeCell badges={[setupStatusBadge(row.setupStatus)]} />
        ),
      },
      {
        key: "provider",
        widthMode: { type: "hug" },
        renderHeaderCell: () => <RedoTableTextCell text="Provider" />,
        renderNormalCell: ({ row }) => (
          <RedoTableTextCell text={PROVIDER_LABEL[row.provider]} />
        ),
      },
      {
        key: "sesDomain",
        widthMode: { type: "fill", relativeLength: 1 },
        renderHeaderCell: () => <RedoTableTextCell text="SES domain" />,
        renderNormalCell: ({ row }) =>
          row.emailDomain ? (
            <RedoTablePaddedCell>
              <Flex dir="column" gap="xs">
                <Text fontSize="sm">{row.emailDomain.domain}</Text>
                <Flex>
                  <RedoTableBadgeCell
                    badges={[dkimBadge(row.emailDomain.dkimStatus)]}
                    cellProps={{ padding: "none" }}
                  />
                </Flex>
              </Flex>
            </RedoTablePaddedCell>
          ) : (
            <RedoTableTextCell text="None" textColor="tertiary" />
          ),
      },
      {
        key: "switch",
        widthMode: { type: "hug" },
        renderHeaderCell: () => <RedoTableTextCell text="" />,
        renderNormalCell: ({ row }) => {
          const target = otherProvider(row.provider);
          const disabledMessage =
            target === "ses" && !row.sesEligible
              ? (row.sesIneligibleReason ?? "This sender can't use SES.")
              : undefined;
          return (
            <RedoTablePaddedCell>
              <DisabledTooltip disabledMessage={disabledMessage}>
                <RedoButton
                  disabled={!!disabledMessage || switchMutation.isPending}
                  hierarchy="secondary"
                  onClick={() =>
                    setPendingSwitch({ sender: row, provider: target })
                  }
                  size="sm"
                  text={`Use ${PROVIDER_LABEL[target]}`}
                />
              </DisabledTooltip>
            </RedoTablePaddedCell>
          );
        },
      },
    ],
    [switchMutation.isPending],
  );

  const senders = sendersQuery.data?.senders ?? [];

  return (
    <>
      <Card
        subtitle="Choose whether each custom email sender sends helpdesk replies through SES or Postmark. Inbound email is unaffected."
        title="Helpdesk email senders"
      >
        <Flex dir="column" gap="md" w="full">
          {sendersQuery.isPending ? (
            <Flex align="center" justify="center" py="lg" w="full">
              <RedoSpinner size="sm" />
            </Flex>
          ) : sendersQuery.isError ? (
            <RedoAlert
              color="error"
              description="Refresh and try again."
              fullWidth
              size="sm"
              title="Could not load email senders"
            />
          ) : senders.length === 0 ? (
            <Text fontSize="sm" textColor="secondary">
              This team has no custom email senders.
            </Text>
          ) : (
            <>
              <RedoTable
                getRowKey={(row) => row.integrationId}
                layout={{ columns, headerColor: "grey" }}
                rowsOfData={senders}
                size="sm"
              />
              {senders
                .filter((sender) => sender.sesReadiness.step !== "ready")
                .map((sender) => (
                  <SesSetupGuide
                    key={sender.integrationId}
                    onRecheck={() => recheckMutation.mutate(sender)}
                    recheckPending={
                      recheckMutation.isPending &&
                      recheckMutation.variables.integrationId ===
                        sender.integrationId
                    }
                    sender={sender}
                    teamId={teamId}
                  />
                ))}
            </>
          )}
        </Flex>
      </Card>
      {pendingSwitch && (
        <RedoModal
          isOpen
          modalSize="sm"
          onModalCloseRequested={() => setPendingSwitch(null)}
          primaryButton={{
            disabled: switchMutation.isPending,
            onClick: () => switchMutation.mutate(pendingSwitch),
            pending: switchMutation.isPending,
            text: `Switch to ${PROVIDER_LABEL[pendingSwitch.provider]}`,
          }}
          secondaryButton={{
            onClick: () => setPendingSwitch(null),
            text: "Cancel",
          }}
          subtitle={`Replies from ${pendingSwitch.sender.email} will send through ${PROVIDER_LABEL[pendingSwitch.provider]} right away. Inbound email keeps arriving the same way.`}
          title="Switch email provider?"
        />
      )}
    </>
  );
});
